/**
 * @typedef {import('./BlogIndexState').default} State
 * @typedef {import('./BlogIndexProps').default} Props
 * @typedef {import('../shared/BlogService/BlogPostExcerpt').default} BlogPostExcerpt
 */

import { translate } from '../App/Internationalization';
import BaseHero from '../shared/BaseHero';
import BlogCategoryFilterSlug from '../config/local/blogCategoryFilter';
import blogConfig from '../config/local/blog';
import BlogPostExcerptCard from '../shared/BlogPostExcerptCard';
import BlogSearchForm from '../Blog/BlogSearchForm';
import captureError from '../utils/captureError';
import images from '../config/local/images';
import Loading from '../shared/Loading';
import NoContent from '../shared/NoContent';
import Pagination from '../shared/Pagination';
import React from 'react';

/** @extends	{React.Component<Props, State>} */
export default class BlogIndex extends React.Component {
	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			allCategorizedPosts: [],
			allCategorizedPostsCategoryId: undefined,
			allTags: [],
			categoryFilters: [],
			currentPageNumber: 1,
			errorMessage: '',
			isGettingData: true,
			lastPageNumber: null,
			lastSearchQuery: '',
			postExcerptList: [],
			selectedCategoryId: undefined,
			selectedTagId: null,
			tagFilterList: [],
		};

		this.handleSearchFormSubmit = this.handleSearchFormSubmit.bind(this);
		this.setCurrentPageNumber = this.setCurrentPageNumber.bind(this);
		this.categoryTagCache = new Map();
		this.latestCategoryTagRequestId = 0;
	}

	componentDidMount() {
		this.initializeData();
	}

	/** @returns {Promise<void>} */
	async initializeData() {
		await Promise.all([
			this.getAndSetPostExcerptList(),
			this.getAllPostTags(),
		]);
	}

	/**
	 * @param {string} [searchQuery]
	 * @param {number} [pageNumber]
	 * @param {string | undefined} [categoryId]
	 * @returns {Promise<BlogPostExcerpt[]>}
	 */
	async getAndSetPostExcerptList(
		searchQuery = this.state.lastSearchQuery,
		pageNumber = this.state.currentPageNumber,
		categoryId = this.state.selectedCategoryId
	) {
		this.setState({ isGettingData: true });
		/** @type {BlogPostExcerpt[]} */
		let fetchedPosts = [];

		try {
			const result = await this.props.blogService.getPostExcerpts(
				searchQuery ?? '',
				pageNumber,
				undefined,
				categoryId ?? undefined
			);
			fetchedPosts = result.posts ?? [];

			this.setState({
				currentPageNumber: pageNumber,
				errorMessage: '',
				lastPageNumber: result.totalPages,
				lastSearchQuery: searchQuery ?? '',
				postExcerptList: fetchedPosts,
				selectedCategoryId: categoryId,
			});
		} catch (error) {
			captureError(error);
			this.setState({
				errorMessage: translate(
					'homeowner.pages.dashboard.marketplace.error'
				),
			});
		} finally {
			this.setState({ isGettingData: false });
		}

		return fetchedPosts;
	}

	/**
	 * @returns {Promise<void>}
	 */
	async getAllPostTags() {
		try {
			const result = await this.props.blogService.getAllPostTags();

			this.setState(
				{
					allTags: result?.tags ?? [],
				},
				() => {
					this.setCategoryFilterList(result?.tags ?? []);
				}
			);
		} catch (error) {
			captureError(error);
			this.setState({
				errorMessage: translate(
					'homeowner.pages.dashboard.marketplace.error'
				),
			});
		} finally {
			this.setState({ isGettingData: false });
		}
	}

	/**
	 * @param {readonly {tagName: string; tagId: string; slug: string }[]} tags
	 */
	setCategoryFilterList(tags) {
		const allowedSet = new Set(BlogCategoryFilterSlug);
		const matchedTags = tags.filter((tag) => allowedSet.has(tag.slug));
		this.setState({
			categoryFilters: matchedTags,
		});
	}

	/**
	 * @param {import('./BlogSearchFormProps').BlogSearchFormValues} values
	 * @returns {void}
	 */
	handleSearchFormSubmit(values) {
		const categoryId = values.categoryId ?? undefined;
		const searchQuery = values.searchQuery ?? '';
		const tagId = values.tagId ?? null;

		if (this.shouldSkipSearchSubmit(searchQuery, categoryId, tagId)) {
			return;
		}

		if (this.handleImmediateSearchSubmit(searchQuery, categoryId, tagId)) {
			return;
		}

		// Keep filter/search selection visible immediately while request is in flight.
		this.setPendingFilterState(searchQuery, categoryId, tagId);

		this.setTagFilterList(searchQuery, categoryId, tagId);
	}

	/**
	 * @param {string} searchQuery
	 * @param {string | undefined} categoryId
	 * @param {string | null} tagId
	 * @returns {boolean}
	 */
	shouldSkipSearchSubmit(searchQuery, categoryId, tagId) {
		return this.hasSameFilterSelection(searchQuery, categoryId, tagId);
	}

	/**
	 * @param {string} searchQuery
	 * @param {string | undefined} categoryId
	 * @param {string | null} tagId
	 * @returns {boolean}
	 */
	handleImmediateSearchSubmit(searchQuery, categoryId, tagId) {
		if (this.handleCategoryCleared(searchQuery, categoryId)) {
			return true;
		}

		if (!this.canFilterLocally(categoryId, tagId)) {
			return false;
		}

		if (categoryId === undefined) {
			return false;
		}

		this.setLocalFilteredPostPage(searchQuery, categoryId, tagId, 1);
		return true;
	}

	/**
	 * @param {string} searchQuery
	 * @param {string | undefined} categoryId
	 * @param {string | null} tagId
	 * @returns {void}
	 */
	setPendingFilterState(searchQuery, categoryId, tagId) {
		this.setState((prevState) => {
			const shouldClearTagFilters =
				categoryId !== prevState.selectedCategoryId;

			return {
				lastSearchQuery: searchQuery,
				selectedCategoryId: categoryId,
				selectedTagId: tagId,
				tagFilterList: shouldClearTagFilters
					? []
					: prevState.tagFilterList,
			};
		});
	}

	/**
	 * @param {string} searchQuery
	 * @param {string | undefined} categoryId
	 * @param {string | null} tagId
	 * @returns {boolean}
	 */
	hasSameFilterSelection(searchQuery, categoryId, tagId) {
		return (
			!searchQuery &&
			this.state.lastSearchQuery === searchQuery &&
			this.state.selectedCategoryId === categoryId &&
			this.state.selectedTagId === tagId
		);
	}

	/**
	 * @param {string} searchQuery
	 * @param {string | undefined} categoryId
	 * @returns {boolean}
	 */
	handleCategoryCleared(searchQuery, categoryId) {
		const isCategoryCleared =
			categoryId === undefined &&
			this.state.selectedCategoryId !== undefined;

		if (!isCategoryCleared) {
			return false;
		}

		this.setState(
			{
				allCategorizedPosts: [],
				allCategorizedPostsCategoryId: undefined,
				lastSearchQuery: searchQuery,
				selectedCategoryId: undefined,
				selectedTagId: null,
				tagFilterList: [],
			},
			() => {
				this.getAndSetPostExcerptList(searchQuery, 1, undefined);
			}
		);

		return true;
	}

	/**
	 * @param {string | undefined} categoryId
	 * @param {string | null} tagId
	 * @returns {boolean}
	 */
	canFilterLocally(categoryId, tagId) {
		return (
			categoryId !== undefined &&
			tagId !== null &&
			this.state.selectedCategoryId === categoryId &&
			this.state.allCategorizedPostsCategoryId === categoryId &&
			this.state.allCategorizedPosts.length > 0
		);
	}

	/**
	 * @param {readonly BlogPostExcerpt[]} categorizedPosts
	 * @returns {{ tagId: string; tagName: string; slug: string }[]}
	 */
	buildTagFilterList(categorizedPosts) {
		const tagIds = new Set();

		for (const post of categorizedPosts) {
			for (const id of post.tagIds ?? []) {
				tagIds.add(id);
			}
		}

		return this.state.allTags.filter((tag) =>
			tagIds.has(Number(tag.tagId))
		);
	}

	/**
	 * @param {string} categoryId
	 * @param {readonly BlogPostExcerpt[]} categorizedPosts
	 * @param {readonly {tagId: string; tagName: string; slug: string }[]} tagFilterList
	 * @returns {void}
	 */
	cacheCategoryTags(categoryId, categorizedPosts, tagFilterList) {
		this.categoryTagCache.set(categoryId, {
			categorizedPosts: [...categorizedPosts],
			tagFilterList: [...tagFilterList],
		});
	}

	/**
	 * @param {string} categoryId
	 * @returns {boolean}
	 */
	applyCachedCategoryTags(categoryId) {
		const cached = this.categoryTagCache.get(categoryId);

		if (!cached) {
			return false;
		}

		this.setState({
			allCategorizedPosts: cached.categorizedPosts,
			allCategorizedPostsCategoryId: categoryId,
			tagFilterList: cached.tagFilterList,
		});

		return true;
	}

	/**
	 * @param {string} categoryId
	 * @param {number} requestId
	 * @returns {Promise<void>}
	 */
	async fetchAndApplyCategoryTags(categoryId, requestId) {
		/** @type {{ results: BlogPostExcerpt[]; }} */
		const data = await this.props.blogService.getAllTagIds(categoryId);
		const categorizedPosts = data?.results ?? [];
		const tagFilterList = this.buildTagFilterList(categorizedPosts);

		this.cacheCategoryTags(categoryId, categorizedPosts, tagFilterList);

		if (requestId !== this.latestCategoryTagRequestId) {
			return;
		}

		if (
			this.state.selectedCategoryId !== categoryId ||
			this.state.selectedTagId !== null
		) {
			return;
		}

		this.setState({
			allCategorizedPosts: categorizedPosts,
			allCategorizedPostsCategoryId: categoryId,
			tagFilterList,
		});
	}

	clearCategoryTags() {
		this.setState({
			allCategorizedPosts: [],
			allCategorizedPostsCategoryId: undefined,
			tagFilterList: [],
		});
	}

	/**
	 * @param {string} searchQuery
	 * @param {string} categoryId
	 * @param {string | null} tagId
	 * @param {number} pageNumber
	 * @returns {void}
	 */
	setLocalFilteredPostPage(searchQuery, categoryId, tagId, pageNumber) {
		const normalizedSearch = searchQuery.trim().toLowerCase();
		const categoryNumber = Number(categoryId);
		const tagNumber = !tagId ? null : Number(tagId);
		const filteredPosts = this.state.allCategorizedPosts.filter((post) =>
			BlogIndex.matchesLocalTagAndSearch(
				post,
				categoryNumber,
				tagNumber,
				normalizedSearch
			)
		);
		const postsPerPage = blogConfig.perPage.listing;
		const lastPageNumber =
			filteredPosts.length > 0
				? Math.ceil(filteredPosts.length / postsPerPage)
				: 0;
		const currentPageNumber =
			lastPageNumber === 0
				? 1
				: Math.min(Math.max(pageNumber, 1), lastPageNumber);
		const startIndex = (currentPageNumber - 1) * postsPerPage;
		const postExcerptList = filteredPosts.slice(
			startIndex,
			startIndex + postsPerPage
		);

		this.setState({
			allCategorizedPostsCategoryId: categoryId,
			currentPageNumber,
			errorMessage: '',
			lastPageNumber,
			lastSearchQuery: searchQuery,
			postExcerptList,
			selectedCategoryId: categoryId,
			selectedTagId: tagId,
		});
	}

	/**
	 * @param {BlogPostExcerpt} post
	 * @param {number} categoryNumber
	 * @param {number | null} tagNumber
	 * @param {string} normalizedSearch
	 * @returns {boolean}
	 */
	static matchesLocalTagAndSearch(
		post,
		categoryNumber,
		tagNumber,
		normalizedSearch
	) {
		const postTagIds = post.tagIds ?? [];
		const hasCategory = postTagIds.includes(categoryNumber);
		const hasTag = tagNumber === null || postTagIds.includes(tagNumber);

		if (!hasCategory || !hasTag) {
			return false;
		}

		if (!normalizedSearch) {
			return true;
		}

		return BlogIndex.matchesLocalSearch(post, normalizedSearch);
	}

	/**
	 * @param {BlogPostExcerpt} post
	 * @param {string} normalizedSearch
	 * @returns {boolean}
	 */
	static matchesLocalSearch(post, normalizedSearch) {
		const postTitle = (post.title ?? '').toLowerCase();
		const postContent = (post.content ?? '').toLowerCase();

		return (
			postTitle.includes(normalizedSearch) ||
			postContent.includes(normalizedSearch)
		);
	}

	/**
	 * @param {string} [searchQuery]
	 * @param {string} [categoryId]
	 * @param {string | null} [tagId]
	 * @returns {Promise<void>}
	 */
	async setTagFilterList(searchQuery, categoryId, tagId) {
		if (categoryId === undefined) {
			this.clearCategoryTags();
			await this.getAndSetPostExcerptList(searchQuery, 1, categoryId);
			return;
		}

		const postRequest = this.getAndSetPostExcerptList(
			searchQuery,
			1,
			categoryId
		);

		if (tagId !== null) {
			await postRequest;
			return;
		}

		if (this.applyCachedCategoryTags(categoryId)) {
			await postRequest;
			return;
		}

		const requestId = this.latestCategoryTagRequestId + 1;
		this.latestCategoryTagRequestId = requestId;
		const tagRequest = this.fetchAndApplyCategoryTags(
			categoryId,
			requestId
		);
		await postRequest;
		await tagRequest;
	}

	/**
	 * @param {number} pageNumber
	 */
	async setCurrentPageNumber(pageNumber) {
		const shouldUseLocalPagination =
			this.state.selectedCategoryId !== undefined &&
			this.state.selectedTagId !== null &&
			this.state.allCategorizedPostsCategoryId ===
				this.state.selectedCategoryId &&
			this.state.allCategorizedPosts.length > 0;

		if (
			shouldUseLocalPagination &&
			this.state.selectedCategoryId !== undefined
		) {
			this.setLocalFilteredPostPage(
				this.state.lastSearchQuery,
				this.state.selectedCategoryId,
				this.state.selectedTagId,
				pageNumber
			);

			return;
		}

		this.setState({ currentPageNumber: pageNumber });
		await this.getAndSetPostExcerptList(
			this.state.lastSearchQuery,
			pageNumber,
			this.state.selectedCategoryId
		);
	}

	/** @returns {JSX.Element} */
	getHeader() {
		return (
			<section className="border-b border-grey-300">
				<section>
					<h2 className="hl-ms-6 mb-4 mt-10">
						{translate('global.blog.title')}
					</h2>
				</section>

				<section className="flex justify-between mb-5">
					<BlogSearchForm
						categoryFilterList={this.state.categoryFilters}
						disabled={
							!!this.state.errorMessage ||
							this.state.isGettingData
						}
						onSubmit={this.handleSearchFormSubmit}
						searchQuery={this.state.lastSearchQuery}
						selectedCategoryId={this.state.selectedCategoryId}
						selectedTagId={this.state.selectedTagId}
						tagFilterList={this.state.tagFilterList}
					/>
					<BaseHero heroImageUrl={images.blog.header} title=" " />
				</section>
			</section>
		);
	}

	render() {
		if (this.state.errorMessage) {
			return (
				<React.Fragment>
					{this.getHeader()}
					<NoContent message={this.state.errorMessage} />
				</React.Fragment>
			);
		}

		if (this.state.isGettingData) {
			return (
				<React.Fragment>
					{this.getHeader()}
					<Loading />
				</React.Fragment>
			);
		}

		if (!this.state.postExcerptList.length) {
			return (
				<React.Fragment>
					{this.getHeader()}
					<NoContent />
				</React.Fragment>
			);
		}

		const postExcerptCardList = this.state.postExcerptList.map(
			(postExcerpt) => (
				<BlogPostExcerptCard
					agentEmail={
						this.props.theme.agentEmail ??
						postExcerpt?.author?.email
					}
					agentSlug={
						this.props.theme.agentSlug ?? postExcerpt?.author?.slug
					}
					authorName={
						this.props.theme.agentName ?? postExcerpt?.author?.name
					}
					content={postExcerpt.content}
					date={postExcerpt.date}
					imageUrl={postExcerpt.imageUrl}
					key={postExcerpt.title}
					slug={postExcerpt.slug}
					title={postExcerpt.title}
				/>
			)
		);

		return (
			<main className="mb-6">
				{this.getHeader()}
				<section className="listing listing-col-size-3 mt-12">
					{postExcerptCardList}
				</section>
				<Pagination
					currentPageNumber={this.state.currentPageNumber}
					lastPageNumber={this.state.lastPageNumber ?? 1}
					setCurrentPageNumber={this.setCurrentPageNumber}
				/>
			</main>
		);
	}
}
